import { Column } from '@tanstack/react-table';
import { Filter, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

const DEFAULT_TITLE = 'Filter';
const DEFAULT_PLACEHOLDER = 'Type to filter...';
const DEFAULT_CLEAR_LABEL = 'Clear filter';

type Props<TData> = {
  column?: Column<TData, unknown>;
  title?: string;
  placeholder?: string;
  clearLabel?: string;
};

export const DataTableTextFilter = <TData,>({
  column,
  title,
  placeholder = DEFAULT_PLACEHOLDER,
  clearLabel = DEFAULT_CLEAR_LABEL,
}: Props<TData>) => {
  const filterValue = (column?.getFilterValue() as string) ?? '';
  const [value, setValue] = useState(filterValue);

  useEffect(() => {
    setValue(filterValue);
  }, [filterValue]);

  const handleValueChange = (nextValue: string) => {
    setValue(nextValue);
    column?.setFilterValue(nextValue.length ? nextValue : undefined);
  };

  const handleClear = () => {
    setValue('');
    column?.setFilterValue(undefined);
  };

  const hasValue = value.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" className="!border-dashed hover:bg-accent">
          <Filter className="h-4 w-4" />
          {title ?? DEFAULT_TITLE}
          {hasValue && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="secondary" className="rounded-md max-w-[140px] truncate font-normal">
                {value}
              </Badge>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[260px] custom-scroll" align="start">
        <DropdownMenuLabel>{title ?? DEFAULT_TITLE}</DropdownMenuLabel>
        <div className="px-1 pb-2">
          <Input
            data-testid={`article-filter-${column?.id}`}
            size="sm"
            autoFocus
            value={value}
            onChange={(event) => handleValueChange(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={placeholder}
            startIcon={<Search className="h-4 w-4" />}
          />
        </div>
        {hasValue && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-sm"
              onSelect={(event) => {
                event.preventDefault();
                handleClear();
              }}
            >
              {clearLabel}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
